import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  VaTextInput,
  VaDate,
  VaLoadingIndicator,
  VaBreadcrumbs,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import SsnField from 'platform/forms-system/src/js/web-component-fields/SsnField';
import { useNavigation } from 'react-router-dom';
import api from '../utilities/api';
import { SEARCH_BC_LABEL, findClaimantBC } from '../utilities/poaRequests';
import { addStyleToShadowDomOnPages } from '../utilities/helpers';
import ClaimantSearchNoPOA from '../components/ClaimantSearchNoPOA';
import ClaimantSearchHasPOA from '../components/ClaimantSearchHasPOA';

// va-date renders Month/Day/Year as separate nested custom elements
// inside its shadow root (rather than exposing one direct input), and
// does not expose a public focus() method. Month ("select-month") is
// targeted directly since that class name is used internally by the
// component's own change-handling logic, making it a stable target.
const focusShadowInput = (selector, nestedSelector) => {
  const host = document.querySelector(selector);

  if (!host?.shadowRoot) {
    host?.focus?.();
    return;
  }

  // Case 1: A specific nested custom element is provided
  // (e.g. va-date's Month field)
  if (nestedSelector) {
    const nestedHost = host.shadowRoot.querySelector(nestedSelector);
    const nestedInput = nestedHost?.shadowRoot?.querySelector('input, select');

    if (nestedInput) {
      nestedInput.focus();
      return;
    }
  }

  // Case 2: A plain input/select directly inside the first shadow root
  const directInput = host.shadowRoot.querySelector('input, select');

  if (directInput) {
    directInput.focus();
    return;
  }

  // Fallback
  host.focus?.();
};

const SearchResults = ({ claimant, searchData }) => {
  return !(claimant?.representative || claimant?.poaRequests?.length) ? (
    <ClaimantSearchNoPOA {...searchData} />
  ) : (
    <ClaimantSearchHasPOA searchData={searchData} claimant={claimant} />
  );
};
SearchResults.propTypes = {
  claimant: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    poaStatus: PropTypes.string,
    representative: PropTypes.string,
    icnTemporaryIdentifier: PropTypes.string,
    poaRequests: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  /* eslint-disable camelcase */
  searchData: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    dob: PropTypes.string,
    ssn: PropTypes.string,
  }),
  /* eslint-able camelcase */
};

const ClaimantSearchPage = title => {
  const [claimant, setClaimant] = useState({});
  const [searchData, setSearchData] = useState(false);
  const [lastSearchData, setLastSearchData] = useState(false);
  const [resetFormKey, setResetFormKey] = useState(0);
  const [validationPerformed, setValidationPerformed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const navigation = useNavigation();
  useEffect(() => {
    document.title = title.title;
    // Insert CSS to hide 'For example: January 19 2000' hint on memorable dates
    // (can't be overridden by passing 'hint' to uiOptions):
    addStyleToShadowDomOnPages(
      [''],
      ['va-date'],
      'va-select::part(label), va-text-input::part(label) {margin-bottom:8px}',
    );
  }, [title]);

  // va-date only shows its internal per-field error styling after a real
  // blur event triggers its own validation, so focus() alone doesn't
  // produce a visible error. This focuses the specific missing sub-field
  // (month/day/year), forces a blur to trigger va-date's internal
  // validation, then refocuses so the user isn't left without focus.
  const focusDobWithValidation = () => {
    const [year, month, day] = (searchData.dob || '').split('-');

    let nestedSelector = 'va-select.select-month';
    if (month && !day) {
      nestedSelector = 'va-select.select-day';
    } else if (month && day && !year) {
      nestedSelector = 'va-text-input.input-year';
    }

    const dobHost = document.querySelector('va-date[name="dob"]');
    const nestedHost = dobHost?.shadowRoot?.querySelector(nestedSelector);
    const input = nestedHost?.shadowRoot?.querySelector('input, select');

    if (input) {
      input.focus();
      input.blur();
      input.focus();
    }
  };

  const isDobComplete = () => {
    const [year, month, day] = (searchData.dob || '').split('-');
    return Boolean(year && month && day);
  };

  const allFieldsPresent = () =>
    searchData.first_name &&
    searchData.last_name &&
    isDobComplete() &&
    searchData.ssn?.length === 9;

  const handleSubmit = e => {
    e.preventDefault();
    setValidationPerformed(true);
    if (allFieldsPresent()) {
      setLoading(true);
      api
        .claimantSearch({ ...searchData })
        .then(response => {
          response.json().then(json => {
            setClaimant(json.data);
          });
        })
        .catch(() => {
          setClaimant(null);
        })
        .finally(() => {
          setLastSearchData({ ...searchData });
          setSearchPerformed(true);
          setLoading(false);
        });
    } else {
      setTimeout(() => {
        if (!searchData.first_name) {
          focusShadowInput('va-text-input[name="first_name"]');
        } else if (!searchData.last_name) {
          focusShadowInput('va-text-input[name="last_name"]');
        } else if (!isDobComplete()) {
          focusDobWithValidation();
        } else if ((searchData.ssn || '').length !== 9) {
          focusShadowInput('va-text-input.masked-ssn');
        }
      }, 100);
    }
    return null;
  };

  const searchStatusText = () =>
    validationPerformed && !allFieldsPresent()
      ? 'Complete the required search fields and try the search again.'
      : '';

  const handleReset = e => {
    e.preventDefault();
    setSearchData({});
    setSearchPerformed(false);
    setValidationPerformed(false);
    // SsnField does not work properly as a controlled element,
    // so changing the key forces React to re-render it
    focusShadowInput('va-text-input[name="first_name"]');
    setResetFormKey(resetFormKey + 1);
    return null;
  };

  const handleChange = field => {
    return e => {
      const updatedSearchData = { ...searchData };
      if (field === 'ssn') {
        updatedSearchData[field] = e;
      } else {
        e.preventDefault();
        updatedSearchData[field] = e.target.value;
      }
      setSearchData(updatedSearchData);
    };
  };

  const searchResult = () =>
    searchPerformed ? (
      <div
        data-testid="claimant-search-page-table-container"
        className="vads-u-display--block"
      >
        <SearchResults claimant={claimant} searchData={lastSearchData} />
      </div>
    ) : (
      searchStatusText()
    );

  return (
    <section className="poa-search">
      <VaBreadcrumbs
        breadcrumbList={findClaimantBC}
        label={SEARCH_BC_LABEL}
        homeVeteransAffairs={false}
        wrapping
      />
      <h1
        data-testid="representation-requests-heading"
        className="poa-request__search-header"
      >
        Find claimant
      </h1>
      <div className="instructional-text va-introtext">
        Find a claimant you represent, or who has submitted a recent request for
        representation.
      </div>
      <form
        role="search"
        method="post"
        id="poa-request__search-form"
        action=""
        onSubmit={handleSubmit}
      >
        <VaTextInput
          label="First name"
          width="xl"
          name="first_name"
          required="required"
          value={searchData.first_name}
          onInput={handleChange('first_name')}
          error={
            validationPerformed && !searchData.first_name
              ? 'Enter a first name'
              : ''
          }
        />
        <VaTextInput
          label="Last name"
          width="xl"
          name="last_name"
          required="required"
          value={searchData.last_name}
          onInput={handleChange('last_name')}
          error={
            validationPerformed && !searchData.last_name
              ? 'Enter a last name'
              : ''
          }
        />
        <VaDate
          className="poa-request-search__date-field"
          label="Date of birth"
          name="dob"
          required="required"
          value={searchData.dob}
          onDateChange={handleChange('dob')}
          error={
            validationPerformed && !isDobComplete()
              ? 'Enter a date of birth'
              : ''
          }
        />
        <SsnField
          key={`ssn-field-key-${resetFormKey}`}
          label="Social Security number"
          name="ssn"
          className="ssn-input"
          required="required"
          uiOptions={{ width: 'xl' }}
          error={
            validationPerformed && (searchData.ssn || '').length !== 9
              ? 'Please enter a valid 9 digit Social Security number (dashes allowed)'
              : ''
          }
          childrenProps={{
            formData: searchData.ssn,
            schema: '',
            idSchema: 'ssn',
            uiSchema: '',
            onChange: handleChange('ssn'),
            onBlur: () => {},
          }}
        />
        <div className="claimant__form-buttons-container">
          <va-button
            text="Search"
            class="claimant__form-submit"
            onClick={handleSubmit}
          />
          <va-button
            text="Clear search"
            class="claimant__form-reset"
            onClick={handleReset}
            secondary
          />
        </div>
      </form>
      {navigation.state === 'loading' || loading ? (
        <VaLoadingIndicator message="Loading..." />
      ) : (
        searchResult()
      )}
    </section>
  );
};

ClaimantSearchPage.loader = async ({ request }) => {
  // Check authorization (403/401 handled by API wrapper)
  const res = await api.checkAuthorized({ signal: request.signal });
  if (res?.status === 401) throw res;
  return null;
};

export default ClaimantSearchPage;
